import { Head, usePage, router } from '@inertiajs/react'
import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { UpgradePlanModal } from '@/components/modals/UpgradePlanModal'
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@/components/ui/resizable'
import type { ImperativePanelHandle } from 'react-resizable-panels'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip'
import { Button } from '@/components/ui/button'
import { Toaster } from '@/components/ui/sonner'
import { toast } from 'sonner'
import { UserAvatarDropdown } from '@/components/ui/user-avatar-dropdown'
import { useIsMobile } from '@/hooks/use-mobile'
import { useConversationChannel } from '@/hooks/useConversationChannel'
import { useProjectChannel } from '@/hooks/useProjectChannel'
import { useConversationState } from '@/hooks/useConversationState'
import { useBrowserUse } from '@/hooks/useBrowserUse'
import { isProductionWorkflowStarting } from '@/lib/workflow-utils'
import { useProjectTasks } from '@/hooks/useProjectTasks'
import { useExcalidrawUse } from '@/hooks/useExcalidrawUse'
import { ChatPane } from '@/components/conversations/ChatPane'
import { PreviewPane } from '@/components/conversations/PreviewPane'
import { PrototypePane } from '@/components/conversations/PrototypePane'
import type { ExcalidrawImperativeAPI } from '@excalidraw/excalidraw/types'
import { MobileConversationView } from '@/components/conversations/MobileConversationView'
import { OrphanedMessagesProvider } from '@/components/orphaned-messages-context'
import { Bot, Monitor, FileText, History, Plug, Globe, PanelLeftOpen, PanelLeftClose, MoreVertical, FolderIcon, Settings } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { Project, Conversation, Message, GroupedTodos, HistoryEntry, Pagination, CreditPack, OrganizationBilling, ActiveWorkflow } from '@/types/conversations'

const STORAGE_KEY = 'cayuProjectChatPanelSizes'
const PARALLEL_WORKER_NAMES = ['neon', 'argon', 'xenon', 'krypton'] as const

interface ProjectChatProps {
  project: Project
  conversations: Conversation[]
  messages: Message[]
  currentConversation: Conversation
  hasProcessingMessages: boolean
  hasMoreMessages: boolean
  groupedTodos: GroupedTodos
  historyEntries: HistoryEntry[]
  historyPagy: Pagination
  orphanedMessageIds?: number[]
  canUndoMessageId?: number | null
  organization?: OrganizationBilling
  creditPacks?: CreditPack[]
  activeWorkflow?: ActiveWorkflow | null
  codingWorkers?: Array<{ id: number; name: string; status: string; preview_url: string; active_task_id: number | null }>
  sketchData?: { elements: any[]; files?: Record<string, any> }
}

export default function Chat({
  project,
  conversations,
  messages: initialMessages,
  currentConversation: initialConversation,
  hasProcessingMessages: initialProcessing,
  hasMoreMessages: initialHasMore,
  groupedTodos: initialTodos,
  historyEntries: initialHistoryEntries,
  historyPagy,
  orphanedMessageIds = [],
  canUndoMessageId: initialCanUndoMessageId = null,
  organization,
  activeWorkflow: initialActiveWorkflow,
  codingWorkers = [],
  creditPacks = [],
  sketchData
}: ProjectChatProps) {
  const { props } = usePage<any>()
  const flash = props.flash
  const { t } = useTranslation('conversations')
  const isMobile = useIsMobile()

  // Panel size state with localStorage persistence
  const [panelSizes, setPanelSizes] = useState<number[]>([33, 67])
  const [isResizeHovered, setIsResizeHovered] = useState(false)
  const [isResizing, setIsResizing] = useState(false)
  const chatPanelRef = useRef<ImperativePanelHandle>(null)
  const previewIframeRef = useRef<HTMLIFrameElement>(null)
  const mobilePreviewIframeRef = useRef<HTMLIFrameElement>(null)

  // Task board for parallel development
  const { tasks: projectTasks, buildingTask, buildingTasks, taskActivities, autoRun, fetchTasks, startTask, markDone, stopTask, acceptTask, discardTask, deleteTask, toggleAutoRun, handleTaskEvent } = useProjectTasks({ projectId: project.id, initialAutoRun: project.auto_run_tasks })
  const [isBoardViewActive, setIsBoardViewActive] = useState(false)
  const [parallelAgentsEnabled, setParallelAgentsEnabled] = useState(project.parallel_agents_enabled)
  const [verificationEnabled, setVerificationEnabled] = useState(project.verification_enabled)
  const [requestChangesTask, setRequestChangesTask] = useState<{ id: number; title: string } | null>(null)

  // Preview control state (lifted from PreviewPane for browser-use control)
  const [deviceView, setDeviceView] = useState<'desktop' | 'mobile'>('desktop')
  const [iframeKey, setIframeKey] = useState(0)
  const [appType, setAppType] = useState<'web' | 'mobile'>('web')

  // Tab state for Preview/Product Spec
  const [activeTab, setActiveTab] = useState('preview')
  // Track visited tabs - mount on first access, stay mounted for scroll preservation
  const [visitedTabs, setVisitedTabs] = useState<Set<string>>(new Set(['preview']))

  const handleTabChange = (tab: string) => {
    setActiveTab(tab)
    setVisitedTabs(prev => {
      if (prev.has(tab)) return prev
      const next = new Set(prev)
      next.add(tab)
      return next
    })
  }

  // Auto-switch to Settings tab if returning from GitHub App installation
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('open_export') === 'true') {
      handleTabChange('settings')
      // Clean up URL to prevent reopening on reload
      params.delete('open_export')
      const newUrl = params.toString()
        ? `${window.location.pathname}?${params}`
        : window.location.pathname
      window.history.replaceState({}, '', newUrl)
    }
  }, [])

  // Responsive tabs - show icon only when space is tight
  const [showTabText, setShowTabText] = useState(true)
  const headerRef = useRef<HTMLElement>(null)

  // Chat panel visibility state
  const [isChatHidden, setIsChatHidden] = useState(false)

  // External content from iframe errors (ErrorBoundary "Send to Chat")
  const [externalPendingContent, setExternalPendingContent] = useState<string | undefined>(undefined)

  // Element selection state
  const [isSelectionModeActive, setIsSelectionModeActive] = useState(false)
  const [selectedElement, setSelectedElement] = useState<any | null>(null)
  const [isSketchViewActive, setIsSketchViewActive] = useState(initialConversation.mode === 'prototype')
  const prevModeRef = useRef(initialConversation.mode)

  // Upgrade modal state (for out of credits warning)
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false)
  const hasShownOutOfCreditsModalRef = useRef(false)

  // Deployment tracking - view deployed entry in history
  const handleViewDeployedInHistory = useCallback(() => {
    handleTabChange('history')
    // Navigate to the correct page where the deployed entry is
    const page = project.deployed_history_entry_page
    if (page && page > 1) {
      router.get(`/projects/${project.id}`, { history_page: page }, { preserveState: true })
    }
  }, [project.id, project.deployed_history_entry_page])

  const csrfToken = () =>
    document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ''

  const toggleSelectionMode = () => {
    const wasActive = isSelectionModeActive
    setIsSelectionModeActive(prev => !prev)
    if (wasActive) {
      setSelectedElement(null)
    }
  }

  const handleElementSelected = (element: any) => {
    setSelectedElement(element)
    setIsSelectionModeActive(false) // Auto-disable after selection
    if (isChatHidden) {
      toggleChatPane()
    }
  }

  const handleCanvasSelectionChange = useCallback((elements: Array<{ id: string; type: string; text?: string }>) => {
    if (elements.length === 0) {
      setSelectedElement((prev: any) => prev?.source === 'canvas' ? null : prev)
    } else {
      setSelectedElement({ source: 'canvas', elements })
    }
  }, [])

  const clearSelectedElement = () => {
    setSelectedElement(null)
  }

  const toggleChatPane = () => {
    const panel = chatPanelRef.current
    if (panel) {
      if (panel.isCollapsed()) {
        panel.expand()
      } else {
        panel.collapse()
      }
    }
  }

  const handleChatPanelCollapse = () => {
    setIsChatHidden(true)
  }

  const handleChatPanelExpand = () => {
    setIsChatHidden(false)
  }

  // Show toast notifications when flash messages change
  useEffect(() => {
    if (!flash) return

    if (flash.success) {
      toast.success(flash.success)
    }

    if (flash.notice) {
      toast.success(flash.notice)
    }

    if (flash.error || flash.alert) {
      const message = flash.error || flash.alert
      toast.error(message)
    }
  }, [flash])

  // Load saved panel sizes from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved) {
      try {
        const sizes = JSON.parse(saved)
        if (Array.isArray(sizes) && sizes.length === 2) {
          setPanelSizes(sizes)
        }
      } catch (e) {
        console.error('Failed to parse saved panel sizes:', e)
      }
    }
  }, [])

  // Monitor header width to show/hide tab text
  useEffect(() => {
    const header = headerRef.current
    if (!header) return

    const observer = new ResizeObserver((entries) => {
      const width = entries[0].contentRect.width
      // Show text when width is above 830px, hide when below (increased for credits/upgrade section)
      setShowTabText(width > 830)
    })

    observer.observe(header)
    return () => observer.disconnect()
  }, [])

  // Listen for error messages from the iframe (target app's ErrorBoundary)
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Handle error messages from the iframe
      if (event.data?.type === 'cayu:error') {
        // Support both 'data' and 'payload' for backward compatibility
        const messageData = event.data.data ?? event.data.payload
        const errorMessage = messageData?.message
        if (errorMessage) {
          // Populate the chat input with the error message
          setExternalPendingContent(errorMessage)
          // Expand chat panel if hidden
          if (isChatHidden) {
            chatPanelRef.current?.expand()
          }
        }
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [isChatHidden])

  // Save panel sizes to localStorage when they change
  const handlePanelResize = (sizes: number[]) => {
    setPanelSizes(sizes)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sizes))
  }

  // State management with ActionCable integration
  const {
    conversation,
    messages,
    groupedTodos,
    processing,
    currentActivity,
    activityHistory,
    isSendingMessage,
    isAcceptingChanges,
    isDiscardingChanges,
    isInterrupting,
    isRetryingWorkflow,
    project: currentProject,
    conversations: _liveConversations,
    historyEntries: liveHistoryEntries,
    organization: liveOrganization,
    hasMoreMessages,
    isLoadingOlder,
    handleChannelEvent,
    handleProjectEvent,
    sendMessage,
    acceptChanges: _acceptChanges,
    discardChanges: _discardChanges,
    approvePlan,
    submitAnswers,
    confirmButton,
    toggleMode,
    switchToMode,
    interruptProcessing,
    retryWorkflow,
    refetchData,
    loadOlderMessages,
    nextStepSuggestions,
    collapsedMessageIds,
    expandedTurns,
    toggleTurnExpansion,
    canUndoMessageId,
    isRewinding,
    setIsRewinding,
    handleUndo,
    orphanedMessageIds: liveOrphanedMessageIds,
    liveCredentials,
    liveProductSpec,
    pmBrowserScreenshots,
    startOptimisticWorkflow,
    activeWorkflow,
    memoryWarning,
    dismissMemoryWarning,
    codingWorkers: liveCodingWorkers,
    provisioningWorkers,
    addProvisioningWorkers,
    removeProvisioningWorkers
  } = useConversationState({
    initialConversation,
    initialMessages,
    initialTodos,
    initialProcessing,
    initialActiveWorkflow: initialActiveWorkflow ?? null,
    initialHasMore,
    projectId: project.id,
    initialProject: {
      id: project.id,
      name: project.name,
      mobile_included: project.mobile_included,
      instance_size: project.instance_size,
      instance_specs: project.instance_sizes?.find(size => size.value === project.instance_size)
    },
    initialConversations: conversations,
    initialHistoryEntries,
    initialOrganization: organization,
    initialOrphanedMessageIds: orphanedMessageIds,
    initialCanUndoMessageId,
    initialCodingWorkers: codingWorkers
  })
  const currentInstanceSize = project.instance_sizes?.find(size => size.value === project.instance_size)
  const supportsLiveEnvironment = project.capabilities.live_environment
  const supportsParallelWorkers = project.capabilities.parallel_workers
  const parallelWorkerCapacity = supportsParallelWorkers ? (currentProject?.instance_specs?.max_workers ?? currentInstanceSize?.max_workers ?? 0) : 0
  const expectedParallelWorkerNames = useMemo(
    () => PARALLEL_WORKER_NAMES.slice(0, Math.max(0, parallelWorkerCapacity)),
    [parallelWorkerCapacity]
  )

  const toggleFeatureSetting = useCallback(async (key: 'parallel_agents_enabled' | 'verification_enabled', value: boolean) => {
    const setter = key === 'parallel_agents_enabled' ? setParallelAgentsEnabled : setVerificationEnabled
    const prev = key === 'parallel_agents_enabled' ? parallelAgentsEnabled : verificationEnabled

    setter(value)

    if (key === 'parallel_agents_enabled') {
      if (value) {
        addProvisioningWorkers(expectedParallelWorkerNames)
      } else {
        removeProvisioningWorkers(expectedParallelWorkerNames)
      }
    }

    try {
      const response = await fetch(`/projects/${project.id}/update_feature_setting`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() },
        body: JSON.stringify({ key, value }),
      })
      if (!response.ok) throw new Error()

      const data = await response.json()
      const actualValue = data[key] ?? value
      setter(actualValue)

      if (key === 'parallel_agents_enabled') {
        if (!actualValue) {
          removeProvisioningWorkers(expectedParallelWorkerNames)
        }
      }
    } catch {
      setter(prev)

      if (key === 'parallel_agents_enabled') {
        if (prev) {
          addProvisioningWorkers(expectedParallelWorkerNames)
        } else {
          removeProvisioningWorkers(expectedParallelWorkerNames)
        }
      }

      toast.error('Failed to update setting')
    }
  }, [
    project.id,
    parallelAgentsEnabled,
    verificationEnabled,
    expectedParallelWorkerNames,
    addProvisioningWorkers,
    removeProvisioningWorkers
  ])

  // Auto-switch to Production tab when a deploy workflow starts
  useEffect(() => {
    if (supportsLiveEnvironment && isProductionWorkflowStarting(activeWorkflow) && activeTab !== 'live') {
      handleTabChange('live')
    }
  }, [activeWorkflow?.id])

  // Auto-switch sketch view when entering/leaving prototype mode
  // Discussion mode uses desktop preview by default; canvas switches on-demand when excalidraw commands arrive
  useEffect(() => {
    const prev = prevModeRef.current
    const curr = conversation.mode

    if (prev !== 'prototype' && curr === 'prototype') {
      setIsSketchViewActive(true)
    } else if (prev === 'prototype' && curr !== 'prototype') {
      setIsSketchViewActive(false)
    }
    prevModeRef.current = curr
  }, [conversation.mode])

  // Preview control callbacks for browser-use
  const handlePreviewReload = useCallback(() => {
    setIframeKey(prev => prev + 1)
  }, [])

  const handleSetViewport = useCallback((viewport: 'desktop' | 'mobile') => {
    setDeviceView(viewport)
  }, [])

  // Switch to mobile app preview and wait for it to be ready
  const handleSwitchToMobile = useCallback(async (): Promise<boolean> => {
    // Check if mobile app is available for this project
    if (!project.django_stack) {
      console.warn('[Chat] Mobile app not available - not a Django stack project')
      return false
    }

    // Switch to mobile app (instant with two-iframe approach)
    setIsSketchViewActive(false)
    setAppType('mobile')
    setDeviceView('mobile')

    // Small delay to ensure state is updated
    return new Promise((resolve) => {
      setTimeout(() => resolve(true), 100)
    })
  }, [project.django_stack])

  // Switch to web app preview with optional viewport
  const handleSwitchToWeb = useCallback(async (viewport?: 'desktop' | 'mobile'): Promise<boolean> => {
    // Switch to web app (instant with two-iframe approach)
    setIsSketchViewActive(false)
    setAppType('web')
    if (viewport) {
      setDeviceView(viewport)
    }

    // Small delay to ensure state is updated
    return new Promise((resolve) => {
      setTimeout(() => resolve(true), 100)
    })
  }, [])

  // Wrap switchToMode to auto-switch to sketch view when entering prototype mode
  const handleSwitchToMode = useCallback(async (mode: string) => {
    if (!project.capabilities?.prototype_mode && mode === 'prototype') return

    await switchToMode(mode)
    if (mode === 'prototype') setIsSketchViewActive(true)
  }, [project.project_type, switchToMode])

  // Switch to sketch view (for browser-use preview control)
  const handleSwitchToSketch = useCallback(async (): Promise<boolean> => {
    if (project.project_type === 'managed_agent') return false

    setIsSketchViewActive(true)
    return new Promise((resolve) => {
      setTimeout(() => resolve(true), 100)
    })
  }, [project.project_type])

  // Handle removing mobile platform from project
  const handleRemoveMobile = useCallback(async () => {
    try {
      const response = await fetch(
        `/projects/${project.id}/features/${conversation.id}/workflow/start`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ''
          },
          body: JSON.stringify({ name: 'remove_mobile' })
        }
      )

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.errors?.[0] || errorData.error || 'Failed to start mobile removal')
      }

      // Switch back to web desktop preview since mobile will be removed
      setAppType('web')
      setDeviceView('desktop')
    } catch (error) {
      console.error('Failed to remove mobile:', error)
    }
  }, [project.id, conversation.id])

  // Browser use testing hook
  const {
    isActive: isBrowserUseTesting,
    actionLog: browserUseActionLog,
    currentAction: browserUseCurrentAction,
    clearLog: _clearBrowserUseLog,
    stopTesting: stopBrowserUseTesting
  } = useBrowserUse({
    projectId: project.id,
    iframeRef: previewIframeRef,
    mobileIframeRef: mobilePreviewIframeRef,
    appType,
    isSketchViewActive,
    onReload: handlePreviewReload,
    onSetViewport: handleSetViewport,
    onSwitchToMobile: handleSwitchToMobile,
    onSwitchToWeb: handleSwitchToWeb,
    onSwitchToSketch: handleSwitchToSketch,
  })

  // Excalidraw state and hook
  const [excalidrawAPI, setExcalidrawAPI] = useState<ExcalidrawImperativeAPI | null>(null)
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const latestSketchDataRef = useRef(sketchData)

  const saveCanvas = useCallback((elements?: readonly any[]) => {
    const api = excalidrawAPI
    if (!api) return

    const sceneElements = elements || api.getSceneElements()
    const visibleElements = sceneElements.filter((e: any) => !e.isDeleted)

    const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ''
    fetch(`/projects/${project.id}/sketch`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
      body: JSON.stringify({
        sketch: {
          scene_data: JSON.stringify({ elements: visibleElements, files: visibleElements.length ? api.getFiles() : {} }),
        },
      }),
    }).then((res) => {
      if (!res.ok) toast.error('Failed to save sketch')
    }).catch(() => {
      toast.error('Failed to save sketch')
    })
  }, [excalidrawAPI, project.id])

  const debouncedSaveCanvas = useCallback((elements: readonly any[]) => {
    const visibleElements = elements.filter((e: any) => !e.isDeleted)
    latestSketchDataRef.current = { elements: visibleElements }
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    saveTimerRef.current = setTimeout(() => saveCanvas(elements), 2000)
  }, [saveCanvas])

  const refreshSketch = useCallback(() => {
    const api = excalidrawAPI
    if (!api) return

    fetch(`/projects/${project.id}/sketch`)
      .then((res) => res.json())
      .then((data) => {
        const elements = data.scene_data?.elements || []
        const files = data.scene_data?.files || {}
        latestSketchDataRef.current = { elements, files }
        api.updateScene({ elements })
        api.addFiles(Object.values(files))
      })
      .catch(() => {
        toast.error('Failed to reload sketch')
      })
  }, [excalidrawAPI, project.id])

  const {
    isActive: isExcalidrawActive,
    currentAction: excalidrawCurrentAction,
  } = useExcalidrawUse({
    projectId: project.id,
    excalidrawAPI,
    onCommandsExecuted: saveCanvas,
    onSwitchToSketch: handleSwitchToSketch,
  })

  // Merge live updates from hook with full project props
  // currentProject contains partial updates (name, mobile_included) from ActionCable
  const displayProject = currentProject ? { ...project, ...currentProject } : project
  const isManagedAgentProject = displayProject.project_type === 'managed_agent'
  const previewTabTitle = isManagedAgentProject ? t('preview.managedAgent.title') : t('preview.title')
  const showProductSpecTab = !isManagedAgentProject
  const showIntegrationsTab = !isManagedAgentProject

  useEffect(() => {
    const tabUnavailable =
      (!supportsLiveEnvironment && activeTab === 'live') ||
      (!showProductSpecTab && activeTab === 'spec') ||
      (!showIntegrationsTab && activeTab === 'integrations')

    if (tabUnavailable) {
      handleTabChange('preview')
    }
  }, [activeTab, supportsLiveEnvironment, showProductSpecTab, showIntegrationsTab])

  useEffect(() => {
    if (isManagedAgentProject && isSketchViewActive) {
      setIsSketchViewActive(false)
    }
  }, [isManagedAgentProject, isSketchViewActive])

  // Get effective organization (live state from hook, or initial prop)
  const effectiveOrganization = liveOrganization || organization

  // Auto-show upgrade modal when paid admin users run out of credits
  useEffect(() => {
    if (!effectiveOrganization) return

    const isPaidUser = effectiveOrganization.current_plan !== 'free'
    const creditsRemaining = Number(effectiveOrganization.credits_remaining) || 0
    const isOutOfCredits = creditsRemaining <= 0
    const isOrgAdmin = props.auth?.user?.organization_role === 'admin'

    // Only show modal for admins who can change the plan
    if (isPaidUser && isOutOfCredits && isOrgAdmin && !hasShownOutOfCreditsModalRef.current) {
      hasShownOutOfCreditsModalRef.current = true
      setIsUpgradeModalOpen(true)
    }
  }, [effectiveOrganization])

  // Track if we've connected before (to distinguish initial connect from reconnect)
  const hasConnectedRef = useRef(false)

  // Intercept import failure to show toast (state update handled by useConversationState)
  const handleChannelEventWithImport = useCallback((event: any) => {
    if (event.type === 'import_progress' && event.data.step === 'failed') {
      toast.error(event.data.message || t('chat.importFailed'))
    }
    if (event.type === 'task_created' || event.type === 'task_updated' || event.type === 'task_activity') {
      handleTaskEvent(event)
    }
    handleChannelEvent(event)
  }, [handleChannelEvent, handleTaskEvent, t])

  useConversationChannel({
    conversationId: conversation.id,
    onEvent: handleChannelEventWithImport,
    onConnected: () => {
      if (hasConnectedRef.current) {
        // This is a REconnection - sync state from server
        // Broadcasts may have been missed while disconnected
        // Critical: prevents form from being enabled while message is still processing
        refetchData()
        fetchTasks()
      } else {
        // First connection
        hasConnectedRef.current = true

        // Check if we have placeholder data that needs fetching
        // (broadcasts may have fired during redirect, before WebSocket connected)
        // Force=true bypasses the processing guard — on first load, processing is always
        // true (first message is being handled) but we still need to fetch the real name
        const hasPlaceholderName = displayProject.name === 'Untitled Project'
        if (hasPlaceholderName) {
          refetchData(true)
        }
      }
    }
  })

  const hasProjectChannelConnectedRef = useRef(false)
  useProjectChannel({
    projectId: project.id,
    onEvent: handleProjectEvent,
    onConnected: () => {
      if (hasProjectChannelConnectedRef.current) {
        refetchData()
      } else {
        hasProjectChannelConnectedRef.current = true
      }
    }
  })

  const title = `${displayProject.name} - Chat`

  // On-demand provisioning: auto-reload while sandbox is being created
  const isProvisioning = project.instance_status === 'provisioning' ||
    project.instance_status === 'setup_in_progress' ||
    project.instance_status === 'pending' ||
    project.instance_status === 'setup_failed'

  useEffect(() => {
    if (!isProvisioning || project.instance_status === 'setup_failed') return
    const interval = setInterval(() => {
      router.reload({ only: ['project'] })
    }, 10000)
    return () => clearInterval(interval)
  }, [isProvisioning, project.instance_status])

  return (
    <OrphanedMessagesProvider orphanedMessageIds={liveOrphanedMessageIds}>
      <Head title={title} />
      <Toaster richColors position="top-right" />
      {isMobile ? (
        /* Mobile View - Tabbed Layout */
        <MobileConversationView
          conversation={conversation}
          project={displayProject}
          isProvisioning={isProvisioning}
          historyEntries={liveHistoryEntries}
          historyPagy={historyPagy}
          messages={messages}
          groupedTodos={groupedTodos}
          processing={processing}
          currentActivity={currentActivity}
          activityHistory={activityHistory}
          isSendingMessage={isSendingMessage}
          isAcceptingChanges={isAcceptingChanges}
          isDiscardingChanges={isDiscardingChanges}
          isInterrupting={isInterrupting}
          onSendMessage={sendMessage}
          onToggleMode={toggleMode}
          onSwitchToMode={handleSwitchToMode}
          onInterrupt={interruptProcessing}
          onApprovePlan={approvePlan}
          onSubmitAnswers={submitAnswers}
          onConfirmButton={confirmButton}
          organization={liveOrganization || organization}
          creditPacks={creditPacks}
          nextStepSuggestions={nextStepSuggestions}
          collapsedMessageIds={collapsedMessageIds}
          expandedTurns={expandedTurns}
          onToggleTurnExpansion={toggleTurnExpansion}
          activeWorkflow={activeWorkflow}
          onAbortWorkflow={interruptProcessing}
          onRetryWorkflow={retryWorkflow}
          hasMoreMessages={hasMoreMessages}
          isLoadingOlder={isLoadingOlder}
          onLoadOlderMessages={loadOlderMessages}
          onRemoveMobile={project.django_stack ? handleRemoveMobile : undefined}
          sketchPane={!isManagedAgentProject ? (
            <PrototypePane
              onAPIReady={setExcalidrawAPI}
              initialData={latestSketchDataRef.current}
              onChange={debouncedSaveCanvas}
              onSelectionChange={handleCanvasSelectionChange}
              isAIDrawing={isExcalidrawActive || processing}
            />
          ) : undefined}
        />
      ) : (
      /* Desktop View - Resizable Panels */
      <div className="flex h-screen w-full bg-background">
        <ResizablePanelGroup
          direction="horizontal"
          className="flex-1"
          onLayout={handlePanelResize}
        >
          {/* Chat Panel (Inset Sidebar Style) */}
          <ResizablePanel
            ref={chatPanelRef}
            defaultSize={panelSizes[0]}
            minSize={30}
            collapsible={true}
            collapsedSize={0}
            onCollapse={handleChatPanelCollapse}
            onExpand={handleChatPanelExpand}
            className="overflow-hidden"
          >
            <div className="flex h-full flex-col bg-content-bg items-center">
              <div className="flex-1 overflow-hidden w-full max-w-3xl">
                  <ChatPane
                    conversation={conversation}
                    project={displayProject}
                    messages={messages}
                    groupedTodos={groupedTodos}
                    processing={processing}
                    buildingTask={buildingTask}
                    buildingTasks={buildingTasks}
                    taskActivities={taskActivities}
                    onStopTask={stopTask}
                    currentActivity={currentActivity}
                    activityHistory={activityHistory}
                    isSendingMessage={isSendingMessage}
                    isAcceptingChanges={isAcceptingChanges}
                    isDiscardingChanges={isDiscardingChanges}
                    isInterrupting={isInterrupting}
                    onSendMessage={sendMessage}
                    onToggleMode={toggleMode}
                    onSwitchToMode={handleSwitchToMode}
                    onInterrupt={interruptProcessing}
                    onApprovePlan={approvePlan}
                    onSubmitAnswers={submitAnswers}
                    onConfirmButton={confirmButton}
                    hasMoreMessages={hasMoreMessages}
                    isLoadingOlder={isLoadingOlder}
                    onLoadOlderMessages={loadOlderMessages}
                    nextStepSuggestions={nextStepSuggestions}
                    collapsedMessageIds={collapsedMessageIds}
                    expandedTurns={expandedTurns}
                    onToggleTurnExpansion={toggleTurnExpansion}
                    externalPendingContent={externalPendingContent}
                    onExternalPendingContentConsumed={() => setExternalPendingContent(undefined)}
                    isSelectionModeActive={project.show_click_to_select && !isSketchViewActive ? isSelectionModeActive : false}
                    onToggleSelectionMode={project.show_click_to_select && !isSketchViewActive ? toggleSelectionMode : undefined}
                    selectedElement={project.show_click_to_select ? selectedElement : undefined}
                    onClearSelectedElement={project.show_click_to_select ? clearSelectedElement : undefined}
                    organizationId={(liveOrganization || organization)?.id}
                    organization={liveOrganization || organization}
                    creditPacks={creditPacks}
                    canUndoMessageId={canUndoMessageId}
                    isRewinding={isRewinding}
                    onUndo={handleUndo}
                    isBrowserUseTesting={isBrowserUseTesting}
                    activeWorkflow={activeWorkflow}
                    requestChangesTask={requestChangesTask}
                    onClearRequestChangesTask={() => setRequestChangesTask(null)}
                    memoryWarning={memoryWarning}
                    onDismissMemoryWarning={dismissMemoryWarning}
                    pmBrowserScreenshots={pmBrowserScreenshots}
                  />
              </div>
            </div>
          </ResizablePanel>

          <ResizableHandle
            className={`w-0 relative bg-transparent transition-all duration-200 cursor-col-resize mt-12 mb-2 ${isChatHidden ? 'pointer-events-none opacity-0' : ''}`}
            onMouseEnter={() => setIsResizeHovered(true)}
            onMouseLeave={() => setIsResizeHovered(false)}
            onDragging={(isDragging) => setIsResizing(isDragging)}
          />

          {/* Preview Panel (Main Content Area) */}
          <ResizablePanel
            defaultSize={panelSizes[1]}
            minSize={30}
            className="overflow-hidden"
          >
            <div className="flex h-full flex-col relative">
              {/* Header */}
              <>
              <header ref={headerRef} className="flex h-12 shrink-0 items-center gap-2 pl-2 pr-4 lg:pr-6 bg-content-bg">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-9 w-9 p-0 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent"
                      onClick={toggleChatPane}
                    >
                      {isChatHidden ? (
                        <PanelLeftOpen className="h-4 w-4" />
                      ) : (
                        <PanelLeftClose className="h-4 w-4" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{isChatHidden ? t('tabs.openChat') : t('tabs.closeChat')}</TooltipContent>
                </Tooltip>

                <div className="flex items-center gap-2">
                  <Tabs value={activeTab} onValueChange={handleTabChange}>
                    <TabsList className="h-9">
                      <TabsTrigger value="preview" className="gap-2" title={previewTabTitle}>
                        {isManagedAgentProject ? <Bot className="h-4 w-4" /> : <Monitor className="h-4 w-4" />}
                        {showTabText && <span>{previewTabTitle}</span>}
                      </TabsTrigger>
                      {showProductSpecTab && (
                        <TabsTrigger value="spec" className="gap-2" title={t('productSpec.title')}>
                          <FileText className="h-4 w-4" />
                          {showTabText && <span>{t('productSpec.title')}</span>}
                        </TabsTrigger>
                      )}
                      <TabsTrigger value="assets" className="gap-2" title={t('tabs.assets')}>
                        <FolderIcon className="h-4 w-4" />
                        {showTabText && <span>{t('tabs.assets')}</span>}
                      </TabsTrigger>
                      {showIntegrationsTab && (
                        <TabsTrigger value="integrations" className="gap-2" title={t('tabs.integrations')}>
                          <Plug className="h-4 w-4" />
                          {showTabText && <span>{t('tabs.integrations')}</span>}
                        </TabsTrigger>
                      )}
                      {supportsLiveEnvironment && (
                        <TabsTrigger value="live" className="gap-2" title={t('tabs.live')}>
                          <span className={`h-1.5 w-1.5 rounded-full flex-shrink-0 ${
                            project.production_status === 'deployed' ? 'bg-green-500'
                              : project.production_status === 'deploy_failed' ? 'bg-red-500'
                              : project.production_status === 'deploying' ? 'bg-blue-500 animate-pulse'
                              : project.production_status === 'suspended_no_credits' ? 'bg-amber-500'
                              : 'bg-gray-400'
                          }`} />
                          <Globe className="h-4 w-4" />
                          {showTabText && <span>{t('tabs.live')}</span>}
                        </TabsTrigger>
                      )}
                    </TabsList>
                  </Tabs>

                  {/* Three-dot menu for additional options */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-9 w-9 p-0 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent"
                      >
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleTabChange('history')}>
                        <History className="mr-2 h-4 w-4" />
                        {t('tabs.history')}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleTabChange('settings')}>
                        <Settings className="mr-2 h-4 w-4" />
                        {t('tabs.settings')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                <div className="ml-auto flex items-center gap-2">
                  <UserAvatarDropdown
                    projectId={project.id}
                    projectName={displayProject.name}
                    organization={liveOrganization || organization}
                  />
                </div>
              </header>

              {/* Scrollable Content Area */}
              <div className="flex flex-1 flex-col overflow-hidden bg-content-bg pr-2 pb-2">
                <div className="flex flex-1 flex-col gap-4">
                  <div
                    className="h-full rounded-xl border bg-card text-foreground overflow-auto relative transition-all duration-200"
                    style={{
                      boxShadow: (isResizeHovered || isResizing)
                        ? '3px 4px 3px 1px rgba(10, 31, 63, 0.5)'
                        : undefined
                    }}
                  >
                    <PreviewPane
                      project={displayProject}
                      isProvisioning={isProvisioning}
                      activeTab={activeTab}
                      isChatHidden={isChatHidden}
                      onToggleChat={toggleChatPane}
                      messages={messages}
                      historyEntries={liveHistoryEntries}
                      historyPagy={historyPagy}
                      processing={processing}
                      groupedTodos={groupedTodos}
                      projectTasks={projectTasks}
                      isBoardViewActive={isBoardViewActive}
                      onBoardViewChange={setIsBoardViewActive}
                      onStartTask={startTask}
                      onMarkDoneTask={markDone}
                      onStopTask={stopTask}
                      onDeleteTask={deleteTask}
                      onAcceptTask={acceptTask}
                      onDiscardTask={discardTask}
                      onRequestChanges={(task) => setRequestChangesTask({ id: task.id, title: task.title })}
                      codingWorkers={liveCodingWorkers}
                      provisioningWorkers={provisioningWorkers}
                      autoRun={autoRun}
                      onToggleAutoRun={toggleAutoRun}
                      parallelAgentsEnabled={parallelAgentsEnabled}
                      parallelWorkerCapacity={parallelWorkerCapacity}
                      onToggleParallelAgents={(v) => toggleFeatureSetting('parallel_agents_enabled', v)}
                      verificationEnabled={verificationEnabled}
                      onToggleVerification={(v) => toggleFeatureSetting('verification_enabled', v)}
                      visitedTabs={visitedTabs}
                      conversationMode={conversation.mode}
                      isSelectionModeActive={project.show_click_to_select ? isSelectionModeActive : false}
                      onElementSelected={project.show_click_to_select ? handleElementSelected : undefined}
                      selectedElement={project.show_click_to_select ? selectedElement : undefined}
                      iframeRef={previewIframeRef}
                      mobileIframeRef={mobilePreviewIframeRef}
                      isBrowserUseTesting={isBrowserUseTesting}
                      browserUseCurrentAction={browserUseCurrentAction}
                      browserUseActionLog={browserUseActionLog}
                      onStopBrowserUseTesting={stopBrowserUseTesting}
                      deviceView={deviceView}
                      onDeviceViewChange={setDeviceView}
                      appType={appType}
                      onAppTypeChange={setAppType}
                      iframeKey={iframeKey}
                      onRefresh={handlePreviewReload}
                      organization={liveOrganization || organization}
                      onViewDeployedInHistory={handleViewDeployedInHistory}
                      isRewinding={isRewinding}
                      onRewindingChange={setIsRewinding}
                      liveCredentials={liveCredentials}
                      liveProductSpec={liveProductSpec}
                      onRemoveMobile={project.django_stack ? handleRemoveMobile : undefined}
                      conversationId={conversation.id}
                      hasSketch={!isManagedAgentProject}
                      isSketchViewActive={isSketchViewActive}
                      isAIDrawing={isExcalidrawActive || processing}
                      aiDrawingAction={excalidrawCurrentAction}
                      onRefreshSketch={refreshSketch}
                      activeWorkflow={activeWorkflow}
                      onWorkflowStart={startOptimisticWorkflow}
                      onAbortWorkflow={interruptProcessing}
                      onRetryWorkflow={retryWorkflow}
                      isAbortingWorkflow={isInterrupting}
                      isRetryingWorkflow={isRetryingWorkflow}
                      onSketchViewChange={setIsSketchViewActive}
                      onInsertPrompt={setExternalPendingContent}
                      sketchPane={!isManagedAgentProject ? (
                        <PrototypePane
                          onAPIReady={setExcalidrawAPI}
                          initialData={latestSketchDataRef.current}
                          onChange={debouncedSaveCanvas}
                          onSelectionChange={handleCanvasSelectionChange}
                          isAIDrawing={isExcalidrawActive || processing}
                        />
                      ) : undefined}
                    />
                  </div>
                </div>
              </div>
                </>
            </div>
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>
      )}

      {/* Upgrade Plan Modal (auto-shows when paid users run out of credits) */}
      <UpgradePlanModal
        open={isUpgradeModalOpen}
        onOpenChange={setIsUpgradeModalOpen}
      />
    </OrphanedMessagesProvider>
  )
}
